/**
 * @deprecated Este arquivo está descontinuado.
 * Use @/utils/formatters e @/utils/validators para novas implementações.
 * 
 * Este arquivo será removido em uma versão futura.
 * Migre seu código para os utilitários centralizados:
 * - formatarCNPJ → @/utils/formatters
 * - validarCNPJ → @/utils/validators
 * - formatarCEP, formatarTelefone → @/utils/formatters
 */

// Re-export das funções centralizadas para compatibilidade temporária
export { 
  validarCNPJ 
} from '@/utils/validators';

export { 
  formatarCNPJ,
  normalizarCNPJ,
  formatarCEP,
  normalizarCEP,
  formatarTelefone,
  normalizarTelefone,
  formatarData
} from '@/utils/formatters';

// Imports internos para uso em funções locais
import { 
  formatarCNPJ as internalFormatarCNPJ,
  normalizarCNPJ as internalNormalizarCNPJ,
  formatarCEP as internalFormatarCEP,
  normalizarCEP as internalNormalizarCEP,
  formatarTelefone as internalFormatarTelefone,
  normalizarTelefone as internalNormalizarTelefone
} from '@/utils/formatters';

// Funções específicas de CNPJ mantidas aqui

export interface CNPJSocio {
  nome: string;
  qualificacao: string;
  cpf_parcial?: string;
  is_administrador: boolean;
}

export interface CNPJData {
  razao_social: string;
  porte?: {
    id?: string;
    descricao?: string;
  };
  natureza_juridica?: {
    id?: string;
    descricao?: string;
  };
  simples?: {
    mei?: boolean | string;
  };
  estabelecimento: {
    cnpj: string;
    nome_fantasia?: string;
    situacao_cadastral: string;
    data_inicio_atividade: string;
    logradouro?: string;
    numero?: string;
    bairro?: string;
    cep?: string;
    ddd1?: string;
    telefone1?: string;
    ddd2?: string;
    telefone2?: string;
    email?: string;
    atividade_principal: {
      id: string;
      descricao: string;
    };
    cidade: {
      nome: string;
    };
    estado: {
      sigla: string;
    };
  };
  socios?: Array<{
    nome: string;
    qualificacao_socio?: {
      id?: string;
      descricao?: string;
    };
    cpf_cnpj_socio?: string;
  }>;
}

export function formatarCPFParcial(cpf: string): string {
  if (!cpf) return '';
  const cpfOnly = cpf.replace(/\D/g, '');
  if (cpfOnly.length !== 11) return cpf;
  return `***${cpfOnly.slice(3, 9)}**`;
}

export function formatarEnderecoLinhaUnica(data: {
  endereco_rua?: string;
  endereco_numero?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_uf?: string;
  endereco_cep_formatado?: string;
}): string {
  const partes = [];
  
  if (data.endereco_rua) {
    let rua = data.endereco_rua;
    if (data.endereco_numero) rua += `, ${data.endereco_numero}`;
    partes.push(rua);
  }
  
  if (data.endereco_bairro) partes.push(data.endereco_bairro);
  
  if (data.endereco_cidade) {
    let cidade = data.endereco_cidade;
    if (data.endereco_uf) cidade += ` - ${data.endereco_uf}`;
    partes.push(cidade);
  }
  
  if (data.endereco_cep_formatado) partes.push(`CEP: ${data.endereco_cep_formatado}`);
  
  return partes.join(', ');
}

export function isAdministrador(qualificacao?: string): boolean {
  if (!qualificacao) return false;
  const qualificacaoLower = qualificacao.toLowerCase();
  return qualificacaoLower.includes('administrador') || 
         qualificacaoLower.includes('admin');
}

export function mapearSocios(data: CNPJData): CNPJSocio[] {
  if (!data.socios || !Array.isArray(data.socios)) return [];
  
  const socios = data.socios.map(socio => ({
    nome: socio.nome || '',
    qualificacao: socio.qualificacao_socio?.descricao || 'Sócio',
    cpf_parcial: socio.cpf_cnpj_socio ? formatarCPFParcial(socio.cpf_cnpj_socio) : undefined,
    is_administrador: isAdministrador(socio.qualificacao_socio?.descricao)
  }));
  
  // Ordenar: Administradores primeiro
  return socios.sort((a, b) => {
    if (a.is_administrador && !b.is_administrador) return -1;
    if (!a.is_administrador && b.is_administrador) return 1;
    return 0;
  });
}

export function mapearDadosCNPJ(data: CNPJData) {
  const est = data.estabelecimento;
  
  return {
    // Dados do cliente
    cnpj: internalNormalizarCNPJ(est.cnpj),
    razao_social: data.razao_social,
    nome_fantasia: est.nome_fantasia || '',
    situacao_cadastral: est.situacao_cadastral,
    data_inicio_atividade: est.data_inicio_atividade,
    cnae_principal: est.atividade_principal?.id || '',
    cnae_descricao: est.atividade_principal?.descricao || '',
    
    // Dados de contato
    telefone_principal: est.ddd1 && est.telefone1 ? internalNormalizarTelefone(est.ddd1, est.telefone1) : '',
    telefone_formatado: est.ddd1 && est.telefone1 ? internalFormatarTelefone(est.ddd1, est.telefone1) : '',
    telefone2: est.ddd2 && est.telefone2 ? internalNormalizarTelefone(est.ddd2, est.telefone2) : '',
    telefone2_formatado: est.ddd2 && est.telefone2 ? internalFormatarTelefone(est.ddd2, est.telefone2) : '',
    email_principal: est.email || '',
    
    // Dados de endereço
    endereco_rua: est.logradouro || '',
    endereco_numero: est.numero || '',
    endereco_bairro: est.bairro || '',
    endereco_cep: est.cep ? internalNormalizarCEP(est.cep) : '',
    endereco_cep_formatado: est.cep ? internalFormatarCEP(est.cep) : '',
    endereco_cidade: est.cidade?.nome || '',
    endereco_uf: est.estado?.sigla || '',
    
    // Novos dados
    mei: data.simples?.mei === "Sim",
    porte: data.porte?.descricao || '',
    natureza_juridica: data.natureza_juridica?.descricao || '',
    socios: mapearSocios(data),
  };
}